import { GetStaticProps } from "next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import Link from "next/link";
import { useEffect, useState } from "react";
import Layout from "../../../components/layout";
import CallToActionBlogBottom from "../../../components/v2/call-to-action-blog-bottom";
import { getProductUpdatesPosts } from "../../../lib/api";
import nextI18NextConfig from "../../../next-i18next.config";
import "../../../styles/scss/blog.module.scss";

const DAYS = [
    { id: "day-5", title: "Day 5: Upgrade in Integrations" },
    { id: "day-4", title: "Day 4: Observability Pipeline" },
    { id: "day-3", title: "Day 3: Endpoint Monitoring" },
    { id: "day-2", title: "Day 2: Notebooks" },
    { id: "day-1", title: "Day 1: AI Dashboard" },
];

export default function ProductUpdates({ allProductUpdates }) {
    const [activeSection, setActiveSection] = useState("day-5");

    useEffect(() => {
        const sections = document.querySelectorAll<HTMLElement>("section[data-day]");

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const day = entry.target.getAttribute("data-day");
                        if (day) {
                            setActiveSection(day);
                        }
                    }
                });
            },
            { rootMargin: "-40% 0px -40% 0px" }
        );

        sections.forEach((section) => observer.observe(section));
        return () => observer.disconnect();
    }, []);


    return (
        <Layout
            isHomePage={false}
            pageTitle="Middleware Launch Week: All Product Updates & Feature Releases"
            ogImage="/images/resources/product-update-og.png"
            metaTitle="Middleware Launch Week: All Product Updates & Feature Releases"
            metaDesc="Catch up on all Middleware Launch Week updates, including new observability features, product improvements, and performance enhancements released throughout the week."
            uri="/product-updates/launch-week/"
            metaRobotsNoindex="index"
            metaRobotsNofollow="follow"
        >
            <div className="middle-wrapper site-page blog-v2">
                <div className="whitepaper-hero-section blog-hero">
                    <div className="whitepaper-search header-section">
                        <div className="container">
                            <div className="d-flex">
                                <div className="left-block">
                                    <h1>
                                        Middleware <span className="text-gradient">Launch Week</span>
                                    </h1>
                                    <p>
                                        Stay updated as we continuously improve, innovate, and bring
                                        you new features.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <section className="single-blog-v2 launch-week-article">
                    <div className="container">
                        <div className="d-flex blog-main-content">
                            {/* TOC */}
                            <div className="left-block">
                                <div className="sticky-share">
                                    <h4>What’s in this article</h4>
                                    <ul>
                                        {DAYS.map((day) => (
                                            <li key={day.id}>
                                                <a
                                                    href={`#${day.id}`}
                                                    className={`toc-link ${activeSection === day.id ? "active" : ""
                                                        }`}
                                                >
                                                    {day.title}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>

                            {/* CONTENT */}
                            <div className="main-content">

                                {/* DAY 5 */}
                                <div
                                    id="day-5"
                                    data-day="day-5"
                                >
                                    <h2>Day 5: Upgrade in Integrations</h2>
                                    <p>
                                        We’ve rolled out two new updates to improve integration and front-end monitoring:</p>
                                    <br />
                                    <h4>Vercel integration (upgraded)</h4>
                                    <p>
                                        You can now install Middleware on Vercel with one click — no code changes required. The app automatically configures log and trace drains so you can view correlated logs and traces out of the box.
                                    </p>
                                    <br />
                                    <p>
                                        We’ve also added support for the Redirect log source, in addition to Static, Lambda, Edge, Build, External, and Firewall.
                                    </p>
                                    <br />
                                    <p>
                                        To get started, check out the documentation <Link href="https://docs.middleware.io/integrations/vercel-integration">
                                            here
                                        </Link>.
                                    </p>
                                    <br />
                                    <h4>RUM for Flutter (new)</h4>

                                    <p>You can now integrate Middleware RUM into your Flutter apps to monitor real user performance. Get session recordings, performance metrics, and correlate front-end errors with backend traces to pinpoint issues and auto-fix them using Ops AI.</p>
                                    <br />
                                    <p>
                                        To configure the flutter application, check the documentation <Link href="https://docs.middleware.io/rum/flutter">
                                            here
                                        </Link>.
                                    </p>
                                    <br />
                                    <div
                                        style={{
                                            position: "relative",
                                            paddingBottom: "63.45%",
                                            height: 0,
                                        }}
                                    >
                                        <iframe
                                            src="https://www.loom.com/embed/05615a6a88c9436d8b8635e81518dcbd"
                                            frameBorder="0"
                                            allowFullScreen
                                            style={{
                                                position: "absolute",
                                                inset: 0,
                                                width: "100%",
                                                height: "100%",
                                            }}
                                        />
                                    </div>

                                    <br />

                                </div>

                                {/* DAY 4 */}
                                <div
                                    id="day-4"
                                    data-day="day-4"
                                >
                                    <h2>Day 4: Observability Pipeline</h2>
                                    <p>
                                        We’ve launched the <b>Middleware Observability Pipeline</b> to help you control and optimize your telemetry data before ingestion.</p>
                                    <br />
                                    <p>
                                        You can define pipeline rules to manage how <b>logs, metrics, and traces</b> are processed and ingested into Middleware. This allows you to filter unnecessary data, apply ingestion controls, and keep only what matters.
                                    </p>
                                    <br />
                                    <p>
                                        The result: lower data volume, reduced costs, and better control over your observability footprint.
                                    </p>
                                    <br />

                                    <div
                                        style={{
                                            position: "relative",
                                            paddingBottom: "63.45%",
                                            height: 0,
                                        }}
                                    >
                                        <iframe
                                            src="https://www.loom.com/embed/7e45d182134e4c6cbfd75bb01b38054d"
                                            frameBorder="0"
                                            allowFullScreen
                                            style={{
                                                position: "absolute",
                                                inset: 0,
                                                width: "100%",
                                                height: "100%",
                                            }}
                                        />
                                    </div>

                                    <br />
                                    <p>
                                        To get started, check out the <Link href="https://docs.middleware.io/admin/settings/pipeline">
                                            documentation
                                        </Link> & <Link href="https://middleware.io/blog/introducing-middleware-observability-pipeline/">
                                            blog
                                        </Link>
                                        .
                                    </p>
                                </div>

                                {/* DAY 3 */}
                                <div
                                    id="day-3"
                                    data-day="day-3"
                                >
                                    <h2>Day 3: Endpoint Monitoring</h2>
                                    <p>
                                        We’ve launched <b>Endpoint Monitoring </b> to help you track and optimize your API performance.</p>
                                    <br />
                                    <p>
                                        You can monitor endpoints directly from your application or upload a list of endpoints manually. Middleware provides key performance metrics such as request rate, success rate, response time, dependencies, errors, etc.
                                    </p>
                                    <br />
                                    <p>
                                        This helps you proactively detect issues, understand endpoint behavior, and ensure your application runs smoothly with reliable API performance.
                                    </p>
                                    <br />

                                    <div
                                        style={{
                                            position: "relative",
                                            paddingBottom: "63.45%",
                                            height: 0,
                                        }}
                                    >
                                        <iframe
                                            src="https://www.loom.com/embed/6fa165fd3d1242dd93d8a13c4de388f3"
                                            frameBorder="0"
                                            allowFullScreen
                                            style={{
                                                position: "absolute",
                                                inset: 0,
                                                width: "100%",
                                                height: "100%",
                                            }}
                                        />
                                    </div>

                                    <br />
                                    <p>
                                        Learn more in the <Link href="https://docs.middleware.io/apm-configuration/endpoints-observability">
                                            documentation
                                        </Link> & <Link href="https://middleware.io/blog/introducing-api-endpoint-monitoring/">
                                            blog
                                        </Link>
                                        .
                                    </p>
                                </div>

                                {/* DAY 2 */}
                                <div
                                    id="day-2"
                                    data-day="day-2"
                                >
                                    <h2>Day 2: Notebooks</h2>
                                    <p>
                                        We’ve launched <b>Logs Notebooks</b> — a new way to investigate and share insights across your data.
                                    </p>
                                    <br />
                                    <p>
                                        You can now create notebooks to combine <b>logs, metrics, and traces,</b> and add notes for full context. This makes it easy to analyze incidents and performance issues in a single, shareable document.
                                    </p>
                                    <br />
                                    <p>
                                        Notebooks support multiple inputs like <b>text, tables, and data widgets,</b> and can be quickly shared with your team for collaboration.
                                    </p>
                                    <br />

                                    <div
                                        style={{
                                            position: "relative",
                                            paddingBottom: "63.45%",
                                            height: 0,
                                        }}
                                    >
                                        <iframe
                                            src="https://www.loom.com/embed/a6bb17443ff6457a97ec71a1361caa6b"
                                            frameBorder="0"
                                            allowFullScreen
                                            style={{
                                                position: "absolute",
                                                inset: 0,
                                                width: "100%",
                                                height: "100%",
                                            }}
                                        />
                                    </div>

                                    <br />
                                    <p>
                                        Learn more in the <Link href="https://docs.middleware.io/workflow/log-monitoring/notebooks">
                                            documentation
                                        </Link> & <Link href="https://middleware.io/blog/introducing-middleware-notebooks/">
                                            blog
                                        </Link>.
                                    </p>
                                </div>

                                {/* DAY 1 */}
                                <div
                                    id="day-1"
                                    data-day="day-1"
                                >
                                    <h2>Day 1: AI Dashboard</h2>

                                    <p>
                                        We’ve launched a new feature:{" "}
                                        <b>Create Dashboards with AI.</b>
                                    </p>
                                    <br />
                                    <p>
                                        With Middleware, you no longer need to manually select
                                        metrics, attributes, filters, or aggregations.{" "}
                                        <b>
                                            Just write a prompt, and Middleware will generate the
                                            dashboard for you.
                                        </b>
                                    </p>
                                    <br />
                                    <p>
                                        You can later edit, refine, or add more widgets using AI or
                                        the traditional manual approach.
                                        <br />
                                        <br />
                                        Build dashboards faster, without SQL queries and no
                                        guesswork.
                                    </p>
                                    <br />

                                    <div
                                        style={{
                                            position: "relative",
                                            paddingBottom: "63.45%",
                                            height: 0,
                                        }}
                                    >
                                        <iframe
                                            src="https://www.loom.com/embed/dd5bdc985b094d3d91714cdf00d66ddd"
                                            frameBorder="0"
                                            allowFullScreen
                                            style={{
                                                position: "absolute",
                                                inset: 0,
                                                width: "100%",
                                                height: "100%",
                                            }}
                                        />
                                    </div>

                                    <br />
                                    <p>
                                        To get started, check out the <Link href="https://docs.middleware.io/workflow/dashboard-builder/build-dashboard-with-ai">
                                            documentation
                                        </Link> & <Link href="https://middleware.io/blog/introducing-fastest-ai-dashboard-builder/">
                                            blog
                                        </Link>.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <CallToActionBlogBottom img="/images/resources/middleware-resource-team.png" />
            </div>

        </Layout>
    );
}

export const getStaticProps: GetStaticProps = async ({
    preview = false,
    locale,
}) => {
    const allProductUpdates = await getProductUpdatesPosts();
    const translations = await serverSideTranslations(
        locale ?? "en",
        ["common", "header", "footer"],
        nextI18NextConfig,
    );
    return {
        props: {
            ...translations,
            allProductUpdates,
            pageTitle: 'Middleware Launch Week: All Product Updates & Feature Releases',
            ogImage: "/images/resources/product-update-og.png",
            metaTitle: 'Middleware Launch Week: All Product Updates & Feature Releases',
            metaDesc: 'Catch up on all Middleware Launch Week updates, including new observability features, product improvements, and performance enhancements released throughout the week.',
            description: 'Catch up on all Middleware Launch Week updates, including new observability features, product improvements, and performance enhancements released throughout the week.',
            uri: '/product-updates/launch-week/',
            metaRobotsNoindex: "index",
            metaRobotsNofollow: "follow"
        },
    };
};